import axios from "axios";

const baseUrl = process.env.REACT_APP_DIFLEX_API_URL + "/users";

// GET
export async function getAllUsers(apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Users/get-all-users`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to fetch users");
  }
}

export async function getUserRoles(apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Role/get-user-roles`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to fetch user roles");
  }
}


export async function getIsUserAdmin(apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Role/is-user-admin`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to fetch data confirming user is admin");
  }
}

export async function getAllRoles(apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/Role/hub/get-all-roles`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to fetch roles");
  }
}

export async function getUsersByTenant(tenantPublicId, apiAccessToken) {
  try {
    const response = await axios.get(
      `${baseUrl}/User/by-tenant/${tenantPublicId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiAccessToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to fetch users");
  }
}

export async function getUsersByAgreement(agreementPublicId, apiAccessToken) {
  try {
    const response = await axios.get(
      `${baseUrl}/User/by-agreement/${agreementPublicId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiAccessToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to fetch users");
  }
}

export async function getUser(userPublicId, apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/User/${userPublicId}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to fetch user");
  }
}

/**
 * Fetches the server-side settings that steer the SCO onboarding form: the activation
 * lead time, its explanatory text, and which list fields accept a value of the
 * orderer's own.
 *
 * @param {string} apiAccessToken - Bearer token for the Insight API
 * @returns {Promise<object>} The SCO form settings
 * @throws {Error} If the API request fails
 */
export async function getScoFormSettings(apiAccessToken) {
  try {
    const response = await axios.get(`${baseUrl}/User/sco-form-settings`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    // The API returns a plain string body for this endpoint, not a JSON error object.
    const detail =
      typeof error.response?.data === "string"
        ? error.response.data
        : error.response?.data?.message;

    throw new Error(detail || "Failed to fetch SCO form settings");
  }
}

// POST
export async function createUser(userData, apiAccessToken) {
  try {
    const response = await axios.post(`${baseUrl}/User`, userData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to create user");
  }
}

export async function createUserHardwareFormSubmit(userData, apiAccessToken) {
  try {
    const response = await axios.post(`${baseUrl}/User/create-user-hardware-form-submit`, userData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to create user");
  }
}

// PUT
export async function updateUser(userPublicId, userData, apiAccessToken) {
  try {
    const response = await axios.put(`${baseUrl}/User/${userPublicId}`, userData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to update user");
  }
}

export async function updateBillingGroupOnUser(
  billingGroupPublicId,
  userPublicId,
  apiAccessToken
) {
  try {
    const requestData = {
      billingGroupPublicId,
      userPublicId,
      updatedDateTimeUTC: new Date().toISOString(),
    };

    const response = await axios.put(
      `${baseUrl}/User/update-billing-group-on-user`,
      requestData,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiAccessToken}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("API Error Response:", error.response?.data);
    throw new Error(error.response?.data?.message || "Failed to update billing group on user"
    );
  }
}

export async function updateBillingGroupAndAccountTypeOnUser(
  requestData,
  apiAccessToken
) {
  try {
    const response = await axios.put(
      `${baseUrl}/User/update-billing-group-and-account-type-on-user`,
      requestData,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiAccessToken}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("API Error Response:", error.response?.data);
    throw new Error(error.response?.data?.message || "Failed to update billing group on user"
    );
  }
}

// DELETE
export async function deleteUser(userId, apiAccessToken) {
  try {
    const response = await axios.delete(`${baseUrl}/User/${userId}`, {
      headers: {
        Authorization: `Bearer ${apiAccessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response.data.message || "Failed to delete user");
  }
}